import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import { type CivilDate } from '~/models/civilDate'
import { type SectionTab } from '~/pages/SplitSupplyPlan/models/period'
import {
  type SplitSupplyPlanListFilters,
  splitSupplyPlanListFiltersCodec,
} from '~/pages/SplitSupplyPlan/models/persistedFilters'

/**
 * 分割供給計画の一覧の絞り込みの保持 key (AC-SC-031: タブ生存期間で消える sessionStorage ではなく
 * localStorage)。 画面ごとに一意。
 */
export const SPLIT_SUPPLY_PLAN_LIST_FILTERS_STORAGE_KEY =
  'ebalance-split-supply-plan-list-filters'

type Result = {
  tab: SectionTab
  setTab: (tab: SectionTab) => void
  /** 開いている断面の対象日 (翌日は受渡日、 月間は対象月の 1 日)。 */
  target: CivilDate
  setTarget: (target: CivilDate) => void
  area: AreaCode | undefined
  setArea: (area: AreaCode | undefined) => void
}

/**
 * 断面のタブ・断面ごとの対象日・エリアの絞り込みを localStorage に保持し、 詳細や取込から戻った時、
 * 再訪した時に復元する。 読み書きのタイミング (未操作の既定値は書かない等) は共通 hook、
 * 直列化・復元・検証は `models/persistedFilters.ts` の codec が持つ。
 */
export function usePersistedSplitSupplyPlanFilters(): Result {
  const { filters, updateFilters } =
    usePersistedFilters<SplitSupplyPlanListFilters>({
      storageKey: SPLIT_SUPPLY_PLAN_LIST_FILTERS_STORAGE_KEY,
      codec: splitSupplyPlanListFiltersCodec,
    })

  return {
    tab: filters.tab,
    setTab: (tab) => updateFilters((prev) => ({ ...prev, tab })),
    target:
      filters.tab === 'monthly'
        ? filters.monthlyTarget
        : filters.dayAheadTarget,
    setTarget: (target) =>
      updateFilters((prev) =>
        prev.tab === 'monthly'
          ? { ...prev, monthlyTarget: target }
          : { ...prev, dayAheadTarget: target },
      ),
    area: filters.area,
    setArea: (area) => updateFilters((prev) => ({ ...prev, area })),
  }
}
